import { Filter } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBox } from "./ErrorBox";
import { FwQueryPager } from "./FwQueryPager";
import { PendingComponent } from "./PendingComponent";
import { Card, CardContent } from "./card";
import { Checkbox } from "./checkbox";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "./table";
import type { FwQueryFilter, FwQueryState } from "./useFwQuery";
import { useTailwindMediaQueries } from "./useTailwindMediaQueries";
import { DataTableAdvancedFilters } from "./DataTableAdvancedFilters";
import { DataTableHeader } from "./DataTableHeader";
import { DataTableQuickFilters } from "./DataTableQuickFilters";
import type { ColumnDef } from "./types";
import type { DataTableState } from "./useDataTable";
import { cn } from "../../../lib/utils";

const PLACEHOLDER_ROW_KEYS = ["row-1", "row-2", "row-3"] as const;

function hasRenderableContent(node: React.ReactNode): boolean {
	if (node === null || node === undefined) {
		return false;
	}
	if (typeof node === "boolean") {
		return false;
	}
	if (typeof node === "string") {
		return node.trim().length > 0;
	}
	if (Array.isArray(node)) {
		return node.some(hasRenderableContent);
	}

	return true;
}

export interface DataTableProps<T = unknown> {
	className?: string;
	dataTableState: DataTableState<T>;
	childrenRightOfDescription?: React.ReactNode;
}

export function DataTable<T = unknown>(props: DataTableProps<T>) {
	const {
		query,
		columns,
		quickFilters,
		filters,
		onRowClick,
		rowClassName,
		emptyMessage = "No items found",
		title,
		description,
		showQuickFilters = true,
		rowSelection,
		showPager = false,
	} = props.dataTableState;

	const { tablet_or_desktop } = useTailwindMediaQueries();
	const isTabletOrDesktop = tablet_or_desktop;

	// State for search and filters
	const [activeQuickFilter, setActiveQuickFilter] = useState<string>("");

	// State for sorting
	const [sortField, setSortField] = useState<string>("");
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

	// State for row selection
	const [selectedRows, setSelectedRows] = useState<T[]>([]);

	// State to store signatures of quick filters (what FwQueryFilter[] they apply)
	const [quickFilterSignatures, setQuickFilterSignatures] = useState<
		Record<string, FwQueryFilter<T>[] | null>
	>({});
	const quickFilterActionInProgress = useRef(false); // Ref to prevent re-entrant calls

	const renderMobileHeader = (column: ColumnDef<T>): React.ReactNode => {
		const header = column.mobile?.header ?? column.header;
		if (typeof header === "function") {
			return header();
		}
		return header;
	};

	const renderMobileCell = (column: ColumnDef<T>, item: T): React.ReactNode => {
		if (column.mobile?.cell) {
			return column.mobile.cell(item);
		}

		if (column.cell) {
			return column.cell(item);
		}

		if (column.accessorKey) {
			const record = item as Record<string, unknown>;
			return record[column.accessorKey as string] as React.ReactNode;
		}

		return null;
	};

	// Effect to generate signatures for quick filters
	useEffect(() => {
		if (!quickFilters) {
			setQuickFilterSignatures({});
			return;
		}
		const signatures: Record<string, FwQueryFilter<T>[] | null> = {};
		quickFilters.forEach((qf) => {
			// Mock FwQueryState to capture what setFiltersAsync would be called with
			const mockQuery = {
				setFiltersAsync: (filtersToSet: FwQueryFilter<T>[]) => {
					signatures[qf.id] = filtersToSet;
				},
				// Use fixed, default values for mock query state to ensure stable signatures
				filters: [] as FwQueryFilter<T>[], // Signature should represent application from a baseline
				pageNo: 1,
				pageSize: 20, // A common default page size
				sort: "",
				// Dummy implementations for other FwQueryState methods
				loadAsync: async () => {
					console.warn(
						"mockQuery.loadAsync called during signature generation for",
						qf.id,
					);
				},
				reset: () => {
					console.warn(
						"mockQuery.reset called during signature generation for",
						qf.id,
					);
				},
				firstPageAsync: async () => {
					console.warn(
						"mockQuery.firstPageAsync called during signature generation for",
						qf.id,
					);
				},
				prevPageAsync: async () => {
					console.warn(
						"mockQuery.prevPageAsync called during signature generation for",
						qf.id,
					);
				},
				nextPageAsync: async () => {
					console.warn(
						"mockQuery.nextPageAsync called during signature generation for",
						qf.id,
					);
				},
				lastPageAsync: async () => {
					console.warn(
						"mockQuery.lastPageAsync called during signature generation for",
						qf.id,
					);
				},
				setPageNoAsync: async (_newPageNo: number) => {
					console.warn(
						"mockQuery.setPageNoAsync called during signature generation for",
						qf.id,
						_newPageNo,
					);
				},
				setPageSizeAsync: async (_newPageSize: number) => {
					console.warn(
						"mockQuery.setPageSizeAsync called during signature generation for",
						qf.id,
						_newPageSize,
					);
				},
				setSortAsync: async (_newSort: string) => {
					console.warn(
						"mockQuery.setSortAsync called during signature generation for",
						qf.id,
						_newSort,
					);
				},
				// Provide all other properties from FwQueryState with default/dummy values
				isPending: false,
				errorMessage: "",
				items: [],
				totalItems: 0,
				totalPages: 0,
				url: "",
				firstBtnDisabled: true,
				prevBtnDisabled: true,
				nextBtnDisabled: true,
				lastBtnDisabled: true,
				pageNoBtnDisabled: true,
				pageSizeBtnDisabled: true,
			};
			try {
				qf.filterFn(mockQuery as unknown as FwQueryState<T>); // Cast to any for the mock
				if (typeof signatures[qf.id] === "undefined") {
					// If filterFn didn't call setFiltersAsync, its signature is considered null
					signatures[qf.id] = null;
				}
			} catch (e) {
				console.error(
					`Error generating signature for quick filter ${qf.id}:`,
					e,
				);
				signatures[qf.id] = null;
			}
		});
		// Only update if signatures actually changed to avoid potential loops if deep comparison is not perfect
		if (JSON.stringify(quickFilterSignatures) !== JSON.stringify(signatures)) {
			setQuickFilterSignatures(signatures);
		}
	}, [quickFilters, quickFilterSignatures]); // Signature generation should only depend on quickFilters definition

	// Effect to synchronize DataTable's internal sort state with query.sort from useFwQuery
	useEffect(() => {
		const currentSortString = query.sort;
		if (currentSortString) {
			const parts = currentSortString.split(" ");
			if (parts.length === 2) {
				const newSortField = parts[0];
				const newSortOrder = parts[1].toLowerCase() as "asc" | "desc";

				if (newSortOrder === "asc" || newSortOrder === "desc") {
					if (sortField !== newSortField || sortOrder !== newSortOrder) {
						setSortField(newSortField);
						setSortOrder(newSortOrder);
					}
				} else {
					// Invalid sort order, clear local sort state
					if (sortField !== "" || sortOrder !== "asc") {
						setSortField("");
						setSortOrder("asc");
					}
				}
			} else {
				// Invalid format, clear local sort state
				if (sortField !== "" || sortOrder !== "asc") {
					setSortField("");
					setSortOrder("asc");
				}
			}
		} else {
			// query.sort is empty, clear local sort state
			if (sortField !== "" || sortOrder !== "asc") {
				setSortField("");
				setSortOrder("asc");
			}
		}
	}, [query.sort, sortField, sortOrder]); // include sortField and sortOrder to avoid stale closures but rely on inner checks

	useEffect(() => {
		// This effect synchronizes the active quick filter button based on query.filters
		// It should run when query.filters changes or when quickFilterSignatures are updated.

		let newActiveQuickFilterId = "";

		if (query.filters && query.filters.length > 0) {
			const currentFiltersString = JSON.stringify(
				query.filters
					.slice()
					.sort((a, b) => String(a.field).localeCompare(String(b.field))),
			);
			for (const qfId in quickFilterSignatures) {
				const signatureFilters = quickFilterSignatures[qfId];
				if (signatureFilters) {
					// Ensure signature filters are also sorted for consistent comparison
					const signatureFiltersString = JSON.stringify(
						signatureFilters
							.slice()
							.sort((a, b) => String(a.field).localeCompare(String(b.field))),
					);
					if (signatureFiltersString === currentFiltersString) {
						newActiveQuickFilterId = qfId;
						break;
					}
				}
			}
			// if (newActiveQuickFilterId) {
			//   // console.log(`DataTable: Matched query.filters to quick filter '${newActiveQuickFilterId}'.`);
			// } else {
			//   console.log(`DataTable: Sync Effect - query.filters present but no matching signature. query.filters:`, JSON.stringify(query.filters), `Signatures:`, JSON.stringify(quickFilterSignatures));
			// }
		} else {
			// query.filters is empty or null
			for (const qfId in quickFilterSignatures) {
				const signatureFilters = quickFilterSignatures[qfId];
				if (signatureFilters && signatureFilters.length === 0) {
					newActiveQuickFilterId = qfId;
					break;
				}
			}
			// if (newActiveQuickFilterId) {
			//   // console.log(`DataTable: query.filters is empty. Matched to quick filter '${newActiveQuickFilterId}' (likely 'All').`);
			// } else if (quickFilters && quickFilters.length > 0 && !activeQuickFilter) {
			//    // This handles the very initial load case where URL is empty, query.filters is empty,
			//    // and no quick filter button is active yet. We want to set the "All" (empty signature) filter active.
			//    // We already tried to find an empty signature above. If not found, this path might be redundant
			//    // or could pick the first if no true "empty signature" filter exists.
			//    // For safety, let's assume the loop above should have found the "All" filter if it exists and has an empty signature.
			//    // If newActiveQuickFilterId is still "", it means no defined QF results in empty filters.
			//    // console.log("DataTable: query.filters empty, no empty signature QF found, and no QF active. No default set by this path.");
			// }
		}

		if (activeQuickFilter !== newActiveQuickFilterId) {
			//console.log(`DataTable: Sync Effect - Changing activeQuickFilter from '${activeQuickFilter}' to '${newActiveQuickFilterId}'. Query Filters:`, JSON.stringify(query.filters));
			setActiveQuickFilter(newActiveQuickFilterId);
		}
	}, [query.filters, quickFilterSignatures, activeQuickFilter]);

	// Function to apply search
	// const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
	//   const value = e.target.value;
	//   setSearchTerm(value);

	//   // Create a filter for searching across multiple fields
	//   if (value) {
	//     // Apply search filter across multiple columns
	//     query.setFiltersAsync([{
	//       field: 'search',
	//       op: 'contains',
	//       value
	//     }]);
	//   } else {
	//     // Clear search filters
	//     query.setFiltersAsync([]);
	//   }
	// }, [query]);

	// Function to apply quick filter
	const handleQuickFilterChange = useCallback(
		async (filterId: string) => {
			// activeQuickFilter below will be from the closure when this useCallback instance was created.
			// This is usually what we want for the guard to prevent re-processing the same ID if already active.
			if (
				/* filterId !== activeQuickFilter && */ !quickFilterActionInProgress.current
			) {
				// Temporarily remove the activeQuickFilter check from guard to see if it's involved in a loop
				// The primary guard against re-entrancy is now quickFilterActionInProgress.
				// We still want to apply the filter even if the button was already active (e.g. user re-clicks "All")

				quickFilterActionInProgress.current = true;
				//console.log(`DataTable: handleQuickFilterChange - Setting activeQuickFilter to '${filterId}'`);
				setActiveQuickFilter(filterId); // Optimistically set button state

				const selectedFilter = quickFilters?.find((f) => f.id === filterId);
				try {
					if (selectedFilter) {
						//console.log(`DataTable: handleQuickFilterChange - Applying filterFn for '${filterId}'`);
						await selectedFilter.filterFn(query);
					} else {
						//console.warn(`DataTable: handleQuickFilterChange - No selectedFilter found for ID '${filterId}'. Applying empty filters.`);
						await query.setFiltersAsync([]);
					}
				} finally {
					quickFilterActionInProgress.current = false;
				}
			}
			// else if (quickFilterActionInProgress.current) {
			//   console.warn(`DataTable: Quick filter change for '${filterId}' ignored, action already in progress.`);
			// } else if (filterId === activeQuickFilter) {
			//   console.log(`DataTable: Quick filter '${filterId}' re-clicked, but no action taken as it is already active and not in progress.`);
			//   // Optionally, you could force a reload if re-clicking an active filter should refresh data:
			//   // quickFilterActionInProgress.current = true;
			//   // await query.loadAsync();
			//   // quickFilterActionInProgress.current = false;
			// }
		},
		[quickFilters, query],
	); // Removed activeQuickFilter from here

	// Function to handle sorting
	const handleSort = useCallback(
		async (columnId: string) => {
			// Find the column to get the correct field name
			const column = columns.find((col) => col.id === columnId);
			if (!column) {
				//console.error(`Column with id ${columnId} not found`);
				return;
			}

			// Use accessorKey as the sort field if available, otherwise use the column id
			const sortFieldName = column.accessorKey?.toString() || columnId;

			//console.log(`⭐ Sort header clicked: Column=${columnId}`);
			//console.log(`Current state - Field: ${sortField}, Order: ${sortOrder}`);

			try {
				//let newSortField = columnId;
				let newSortOrder: "asc" | "desc" = "asc";

				// Toggle order if the same column is clicked
				if (sortField === columnId) {
					// Flip the sort order
					newSortOrder = sortOrder === "asc" ? "desc" : "asc";
					//console.log(`⮎ Same column clicked - changing order to ${newSortOrder}`);
				} else {
					// New column selected - default to ascending
					//console.log(`⮎ New column selected - setting order to asc`);
				}

				// Create the sort string that will be used for the API request
				const sortString = `${sortFieldName} ${newSortOrder}`;
				//console.log(`🔄 Final sort string: "${sortString}"`);

				// Update UI state FIRST to keep the UI responsive
				setSortField(columnId);
				setSortOrder(newSortOrder);

				// Then perform the API request with our sort string
				await query.setSortAsync(sortString);

				//console.log('✅ Sort operation completed');
			} catch (error) {
				console.error("❌ Error during sorting:", error);
			}
		},
		[query, sortField, sortOrder, columns],
	);

	// Helper function to get row ID
	const getRowId = useCallback(
		(item: T): string => {
			if (!rowSelection) return "";

			// Handle ID field function or key
			if (typeof rowSelection.idField === "function") {
				return rowSelection.idField(item);
			} else if (rowSelection.idField) {
				return String(item[rowSelection.idField]);
			}

			// Default to index in query.items
			return String(query.items.indexOf(item));
		},
		[rowSelection, query.items],
	);

	// Function to check if a row is selected
	const isRowSelected = useCallback(
		(item: T): boolean => {
			if (!rowSelection?.enabled) return false;

			const itemId = getRowId(item);
			return selectedRows.some((row) => getRowId(row) === itemId);
		},
		[rowSelection, selectedRows, getRowId],
	);

	// Function to toggle row selection
	const toggleRowSelection = useCallback(
		(item: T, event?: React.MouseEvent): void => {
			if (!rowSelection?.enabled) return;

			// Stop event propagation if it exists
			if (event) {
				event.stopPropagation();
			}

			const itemId = getRowId(item);

			if (rowSelection.multiple) {
				// Toggle multiple selection
				setSelectedRows((prev) => {
					const isSelected = prev.some((row) => getRowId(row) === itemId);

					if (isSelected) {
						// Remove from selection
						const newSelection = prev.filter((row) => getRowId(row) !== itemId);

						// Notify about change
						if (rowSelection.onSelectionChange) {
							rowSelection.onSelectionChange(newSelection);
						}

						return newSelection;
					} else {
						// Add to selection
						const newSelection = [...prev, item];

						// Notify about change
						if (rowSelection.onSelectionChange) {
							rowSelection.onSelectionChange(newSelection);
						}

						return newSelection;
					}
				});
			} else {
				// Single selection
				setSelectedRows((prev) => {
					const isSelected = prev.length === 1 && getRowId(prev[0]) === itemId;

					if (isSelected) {
						// Deselect
						if (rowSelection.onSelectionChange) {
							rowSelection.onSelectionChange([]);
						}
						return [];
					} else {
						// Select only this row
						if (rowSelection.onSelectionChange) {
							rowSelection.onSelectionChange([item]);
						}
						return [item];
					}
				});
			}
		},
		[rowSelection, getRowId],
	);

	// Function to handle select all rows
	const toggleSelectAll = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			if (!rowSelection?.enabled || !rowSelection.multiple) return;

			if (event.target.checked) {
				// Select all
				setSelectedRows(query.items);
				if (rowSelection.onSelectionChange) {
					rowSelection.onSelectionChange(query.items);
				}
			} else {
				// Deselect all
				setSelectedRows([]);
				if (rowSelection.onSelectionChange) {
					rowSelection.onSelectionChange([]);
				}
			}
		},
		[rowSelection, query.items],
	);

	// Prep columns with selection checkbox if needed
	const effectiveColumns = useMemo(() => {
		if (!rowSelection?.enabled) return columns;

		// Create selection column
		const selectionColumn: ColumnDef<T> = {
			id: "selection",
			header: rowSelection.multiple
				? // For multiple selection, include a header checkbox for select all
					() => (
						<Checkbox
							checked={
								query.items.length > 0 &&
								selectedRows.length === query.items.length
							}
							indeterminate={
								selectedRows.length > 0 &&
								selectedRows.length < query.items.length
							}
							onCheckedChange={(checked) =>
								toggleSelectAll({
									target: { checked: !!checked },
								} as React.ChangeEvent<HTMLInputElement>)
							}
						/>
					)
				: // For single selection, just leave header empty
					"",
			sortable: false,
			className: "w-[50px]",
			cell: (item: T) => (
				<Checkbox
					checked={isRowSelected(item)}
					onCheckedChange={() => toggleRowSelection(item)}
					onClick={(e) => e.stopPropagation()}
				/>
			),
		};

		// Add selection column to beginning of columns
		return [selectionColumn, ...columns];
	}, [
		columns,
		rowSelection,
		isRowSelected,
		toggleRowSelection,
		selectedRows,
		query.items,
		toggleSelectAll,
	]);

	const mobileColumns = useMemo(() => {
		return effectiveColumns
			.map((column, index) => ({ column, index }))
			.filter(({ column }) => column.mobile?.hide !== true)
			.sort((a, b) => {
				const orderA = a.column.mobile?.order ?? a.index;
				const orderB = b.column.mobile?.order ?? b.index;
				return orderA - orderB;
			})
			.map(({ column }) => column);
	}, [effectiveColumns]);

	const mobileColumnsToRender =
		mobileColumns.length > 0 ? mobileColumns : effectiveColumns;
	return (
		<div
			className={cn("space-y-4 flex-1 min-h-0 flex flex-col", props.className)}
		>
			<div className="flex flex-wrap justify-between items-center">
				<div className="w-full flex flex-wrap justify-between text-2xl font-bold px-2 pt-2 pb-2">
					<div>{title}</div>
					{filters && filters.length > 0 && (
						<div className="flex items-end">
							<DataTableAdvancedFilters<T> filters={filters} query={query} />
						</div>
					)}
				</div>

				<div className="w-full gap-2 items-center pt-2 pb-2">
					{filters && filters.length > 0 && (
						<div className="flex flex-wrap justify-between items-center">
							{description && (
								<div className="text-sm px-2 pb-2">{description}</div>
							)}

							{props.childrenRightOfDescription && (
								<div className="flex items-center gap-2">
									{props.childrenRightOfDescription}
								</div>
							)}

							{showQuickFilters && quickFilters && quickFilters.length > 0 && (
								<div className="flex items-center gap-2">
									<Filter className="h-4 w-4 text-muted-foreground" />
									<DataTableQuickFilters
										quickFilters={quickFilters}
										activeFilter={activeQuickFilter}
										onChange={handleQuickFilterChange}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			<Card className="p-0 flex-1 min-h-0 flex flex-col">
				<CardContent className="p-0 flex-1 min-h-0 flex flex-col">
					<div className="rounded-md border relative flex-1 min-h-0 flex flex-col">
						{/* Loading overlay that appears while keeping the existing data visible */}
						{query.isPending && (
							<div className="flex-1 absolute inset-0 bg-background/80 z-10 flex items-center justify-center backdrop-blur-[1px]">
								<PendingComponent message={`Loading ${title}...`} />
							</div>
						)}

						<div className="flex-1 min-h-0 overflow-hidden">
							{isTabletOrDesktop ? (
								<Table containerClassName="h-full overflow-auto">
									<TableHeader>
										<TableRow>
											{/* Regular columns (including selection if enabled) */}
											{effectiveColumns.map((column) => (
												<DataTableHeader
													key={column.id}
													column={column}
													onSort={
														column.sortable !== false
															? () => handleSort(column.id)
															: undefined
													}
													isSorted={sortField === column.id}
													isSortedDesc={
														sortField === column.id && sortOrder === "desc"
													}
												/>
											))}
										</TableRow>
									</TableHeader>
									<TableBody>
										{/* Show a placeholder row if there's no data and we're not loading */}
										{query.errorMessage && (
											<TableRow>
												<TableCell
													colSpan={effectiveColumns.length}
													className="text-center py-8 text-muted-foreground"
												>
													<ErrorBox message={query.errorMessage} />
												</TableCell>
											</TableRow>
										)}
										{!query.errorMessage &&
											!query.isPending &&
											query.items.length === 0 && (
												<TableRow>
													<TableCell
														colSpan={effectiveColumns.length}
														className="text-center py-8 text-muted-foreground"
													>
														{emptyMessage}
													</TableCell>
												</TableRow>
											)}
										{!query.errorMessage &&
											query.items.length > 0 &&
											/* Always render the items array (which might be the previous page data during loading) */
											query.items.map((item, index) => (
												<TableRow
													key={getRowId(item) || index}
													onClick={() => onRowClick?.(item)}
													className={`${onRowClick ? "cursor-pointer" : ""} ${rowClassName ? rowClassName(item) : ""} ${isRowSelected(item) ? "bg-muted/50" : ""}`}
												>
													{/* All cells (including selection if enabled) */}
													{effectiveColumns.map((column) => (
														<TableCell
															key={`${index}-${column.id}`}
															className={column.className}
														>
															{
																(column.cell
																	? column.cell(item)
																	: column.accessorKey
																		? (item as Record<string, unknown>)[
																				column.accessorKey as string
																			]
																		: null) as React.ReactNode
															}
														</TableCell>
													))}
												</TableRow>
											))}

										{/* If we're in a loading state and have no previous data, show a minimum number of placeholder rows */}
										{query.isPending &&
											query.items.length === 0 &&
											PLACEHOLDER_ROW_KEYS.map((placeholderKey) => (
												<TableRow key={placeholderKey} className="opacity-50">
													{effectiveColumns.map((column) => (
														<TableCell
															key={`${placeholderKey}-${column.id}`}
															className={column.className}
														>
															<div className="h-6 w-full">&nbsp;</div>
														</TableCell>
													))}
												</TableRow>
											))}
									</TableBody>
								</Table>
							) : (
								<div className="h-full flex flex-1 flex-col min-h-0 overflow-y-auto">
									{query.errorMessage ? (
										<div className="px-4 py-6">
											<ErrorBox message={query.errorMessage} />
										</div>
									) : !query.errorMessage &&
										!query.isPending &&
										query.items.length === 0 ? (
										<div className="py-8 px-4 text-center text-muted-foreground">
											{emptyMessage}
										</div>
									) : query.items.length > 0 ? (
										<ul className="flex flex-col divide-y">
											{query.items.map((item, index) => {
												const key = getRowId(item) || index;
												const rowIsSelected = isRowSelected(item);
												const rowClasses = [
													"p-4 flex flex-col gap-3 transition-colors",
													onRowClick ? "cursor-pointer" : "",
													rowClassName ? rowClassName(item) : "",
													rowIsSelected ? "bg-muted/50" : "",
												]
													.filter(Boolean)
													.join(" ");

												return (
													<li
														key={key}
														onClick={() => onRowClick?.(item)}
														onKeyDown={(event) => {
															if (event.key === "Enter" || event.key === " ") {
																event.preventDefault();
																onRowClick?.(item);
															}
														}}
														tabIndex={onRowClick ? 0 : undefined}
														className={rowClasses}
													>
														{mobileColumnsToRender.map((column) => {
															const labelContent = renderMobileHeader(column);
															const shouldShowLabel = !(
																column.mobile?.hideLabel ??
																!hasRenderableContent(labelContent)
															);
															const wrapperClass = [
																"flex flex-col gap-1",
																column.mobile?.wrapperClassName ?? "",
															]
																.filter(Boolean)
																.join(" ");
															const valueClass = column.mobile?.className ?? "";

															return (
																<div
																	key={`${key}-${column.id}`}
																	className={wrapperClass}
																>
																	{shouldShowLabel && (
																		<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
																			{labelContent}
																		</div>
																	)}
																	<div className={valueClass}>
																		{renderMobileCell(column, item)}
																	</div>
																</div>
															);
														})}
													</li>
												);
											})}
										</ul>
									) : (
										<div className="flex flex-col divide-y">
											{PLACEHOLDER_ROW_KEYS.map((placeholderKey) => (
												<div
													key={placeholderKey}
													className="p-4 space-y-2 opacity-60"
												>
													{mobileColumnsToRender.map((column) => (
														<div
															key={`${placeholderKey}-${column.id}`}
															className="flex flex-col gap-1"
														>
															<div className="h-3 w-1/3 rounded bg-muted/40" />
															<div className="h-5 w-full rounded bg-muted/30" />
														</div>
													))}
												</div>
											))}
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{showPager && <FwQueryPager query={query} />}
		</div>
	);
}

// Add display name for debugging
DataTable.displayName = "DataTable";
